import { useEffect, useState } from "react";
import { FileText, Plus, Trash2, Pencil, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Modal, PhotoUpload, inp, lbl } from "./GearBits";

const DOC_TYPES = [
  ["passport", "Passport"], ["visa", "Visa"], ["id_card", "ID card"], ["drivers_license", "Driver's license"],
  ["travel_insurance", "Travel insurance"], ["health_document", "Health document"], ["vaccination", "Vaccination record"],
  ["ticket", "Ticket"], ["reservation", "Reservation"], ["other", "Other"],
];
const typeLabel = (k) => (DOC_TYPES.find(([t]) => t === k) || ["", k])[1];

const ExpiryBadge = ({ d }) => {
  if (d.expiry_state === "expired")
    return <span className="text-[10px] font-900 uppercase tracking-widest bg-red-100 text-red-700 px-2 py-0.5" data-testid={`doc-expired-${d.id}`}>Expired</span>;
  if (d.expiry_state === "expiring")
    return <span className="text-[10px] font-900 uppercase tracking-widest bg-amber-100 text-amber-700 px-2 py-0.5" data-testid={`doc-expiring-${d.id}`}>{d.days_to_expiry} days left</span>;
  if (d.expiry_state === "ok")
    return <span className="text-[10px] font-900 uppercase tracking-widest bg-emerald-100 text-emerald-700 px-2 py-0.5">Valid</span>;
  return null;
};

export default function DocumentVault({ travelers }) {
  const [docs, setDocs] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => api.get("/gear/documents").then((r) => setDocs(r.data.items)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);

  const del = async (d) => {
    if (!window.confirm(`Delete this ${typeLabel(d.doc_type)}? Its expiry reminders will be cancelled.`)) return;
    await api.delete(`/gear/documents/${d.id}`);
    load();
  };

  return (
    <div data-testid="document-vault">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-xl">
          <BellRing className="w-4 h-4 inline mr-1 text-primary" />
          Add an expiry date and we'll remind you <strong>90 and 30 days before</strong> (in-app + email).
          Many countries require 6 months of passport validity — never let a renewal sneak up.
        </p>
        <button onClick={() => setForm({})} data-testid="doc-new"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-primary text-white hover:bg-blue-700 shrink-0">
          <Plus className="w-4 h-4" /> Add document
        </button>
      </div>

      {docs && docs.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-10 text-center text-muted-foreground text-sm">
          <FileText className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
          No documents yet — add passports, visas, insurance and licenses for every traveler.
        </div>
      )}
      {docs && docs.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {docs.map((d) => (
            <div key={d.id} className="border border-zinc-300 bg-white p-4" data-testid={`doc-card-${d.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <span className="font-heading font-900 text-sm">{typeLabel(d.doc_type)}</span>
                <span className="ml-auto"><ExpiryBadge d={d} /></span>
              </div>
              <div className="text-sm">{d.holder_name || "—"}</div>
              <div className="text-xs text-muted-foreground mt-0.5 space-x-2">
                {d.number && <span>№ {d.number}</span>}
                {d.issuing_country && <span>{d.issuing_country}</span>}
                {d.expiry_date && <span>expires {d.expiry_date}</span>}
              </div>
              <div className="flex justify-end gap-1 mt-2 border-t border-zinc-100 pt-1.5">
                <button onClick={() => setForm(d)} data-testid={`doc-edit-${d.id}`} className="p-1.5 text-zinc-400 hover:text-primary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(d)} data-testid={`doc-delete-${d.id}`} className="p-1.5 text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {form !== null && <DocForm doc={form} travelers={travelers} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

function DocForm({ doc, travelers, onClose, onSaved }) {
  const [f, setF] = useState({ doc_type: "passport", holder_name: "", owner_id: "", number: "",
    issuing_country: "", issue_date: "", expiry_date: "", photo_url: null, notes: "", ...doc });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!f.holder_name.trim() && !f.number.trim()) return toast.error("Add at least the holder's name or the document number");
    setBusy(true);
    try {
      const owner = travelers.find((t) => t.id === f.owner_id);
      const payload = { ...f, owner_name: owner?.name || null, owner_id: f.owner_id || null };
      if (doc.id) await api.put(`/gear/documents/${doc.id}`, payload);
      else await api.post("/gear/documents", payload);
      toast.success(f.expiry_date ? "Saved — expiry reminders scheduled (90 & 30 days before)" : "Document saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save document"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={doc.id ? "Edit document" : "Add a travel document"} onClose={onClose}>
      <div className="space-y-3" data-testid="doc-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Document type</label>
            <select value={f.doc_type} onChange={set("doc_type")} data-testid="doc-type" className={inp}>
              {DOC_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className={lbl}>Holder (traveler)</label>
            <div className="flex gap-2">
              <select value={f.owner_id || ""} onChange={(e) => {
                const t = travelers.find((x) => x.id === e.target.value);
                setF({ ...f, owner_id: e.target.value, holder_name: f.holder_name || t?.name || "" });
              }} className={`${inp} max-w-[110px]`}>
                <option value="">Pick…</option>
                {travelers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <input value={f.holder_name || ""} onChange={set("holder_name")} data-testid="doc-holder" className={inp} placeholder="Name on document" />
            </div></div>
          <div><label className={lbl}>Document number</label><input value={f.number || ""} onChange={set("number")} data-testid="doc-number" className={inp} /></div>
          <div><label className={lbl}>Issuing country</label><input value={f.issuing_country || ""} onChange={set("issuing_country")} data-testid="doc-country" className={inp} placeholder="United States" /></div>
          <div><label className={lbl}>Issue date</label><input type="date" value={f.issue_date || ""} onChange={set("issue_date")} className={inp} /></div>
          <div><label className={lbl}>Expiry date (for reminders)</label><input type="date" value={f.expiry_date || ""} onChange={set("expiry_date")} data-testid="doc-expiry" className={inp} /></div>
        </div>
        <PhotoUpload value={f.photo_url} onChange={(u) => setF({ ...f, photo_url: u })} testid="doc-photo-upload" />
        <div><label className={lbl}>Notes</label><textarea value={f.notes || ""} onChange={set("notes")} rows={2} className={inp} placeholder="Where the original is kept, copies made…" /></div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="doc-save" className="px-5 py-2 text-sm font-bold bg-primary text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? "Saving…" : "Save document"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
